import { UserContext } from '@celito.clients/provider';
import { SelectionEvents } from '@fluentui/react-combobox';
import { useContext, useState } from 'react';

import { RowItem } from '../../grid-view-new/src/types';
import { IOption } from '../../in-house-input-select/in-house-input-select.model';
import { ICommonData } from '../../in-house-input-select/services';
import {
  ReferenceSelectorItem,
  ReferenceSelectorProps,
} from './reference-selector.model';
import ReferenceSelectorView from './reference-selector.view';

interface ReferenceSelectorControllerProps extends ReferenceSelectorProps {}

export const ReferenceSelectorController = (
  props: ReferenceSelectorControllerProps
): JSX.Element => {
  const [isShowAllResultsViewOpen, setIsShowAllResultsViewOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInlineCreationDialogOpen, setIsInlineCreationDialogOpen] =
    useState(false);

  // Get user's domain objects to check create permission
  const { domainObjects } = useContext(UserContext);

  // Check if user has create permission for the related object
  const hasCreatePermission = !!domainObjects?.find(
    (obj) => obj.name === props.objectName
  )?.hasCreatePermission;

  const [searchKey, setSearchKey] = useState('');
  const onRemoveItem = (item: RowItem) => {
    props.onRemoveItem(item);
    props.onSelectorChange(
      props.selectedItems.filter((itm) => {
        if (itm?.version && item?.version) {
          return (
            `${itm.name}-${itm.version}` !== `${item.name}-${item.version}`
          );
        }
        return itm.name !== item.name;
      })
    );
  };

  const handleOnAddClick = (items: ReferenceSelectorItem[]) => {
    const newItems = items;

    const updatedSelectedItems = props.referencePickerProps.multiselect
      ? [
          ...(Array.isArray(props.selectedItems) ? props.selectedItems : []),
          ...newItems,
        ]
      : newItems;

    props.onSelectorChange(updatedSelectedItems);

    onShowAllResult();
  };

  const onOptionSelect = (
    _ev: SelectionEvents,
    optionText: string,
    optionValue: string,
    selectedOptions: IOption[] | IOption,
    data?: ICommonData
  ) => {
    const isRemoval = props?.selectedItems?.some(
      (selected) => selected.name === optionValue
    );

    const selectedItem: ReferenceSelectorItem = {
      key: optionValue,
      label: optionText,
      name: optionValue,
      ...data,
    };

    const updatedSelectedItems = props.referencePickerProps.multiselect
      ? [
          ...(Array.isArray(props.selectedItems) ? props.selectedItems : []),
          selectedItem,
        ]
      : [selectedItem];

    const newSelectedOptions = isRemoval
      ? props.selectedItems?.filter((selected) => selected.name !== optionValue)
      : updatedSelectedItems;

    props.onSelectorChange(newSelectedOptions);
  };

  const onShowAllResult = () => {
    setIsShowAllResultsViewOpen((prev) => !prev);
  };

  // Inline creation handlers
  const handleInlineCreationOpen = () => {
    setIsInlineCreationDialogOpen(true);
  };

  const handleInlineCreationClose = () => {
    setIsInlineCreationDialogOpen(false);
  };

  const handleRecordCreated = (newRecord: ReferenceSelectorItem) => {
    // Auto-add the newly created record to selected items
    const updatedSelectedItems = props.referencePickerProps.multiselect
      ? [
          ...(Array.isArray(props.selectedItems) ? props.selectedItems : []),
          newRecord,
        ]
      : [newRecord];

    props.onSelectorChange(updatedSelectedItems);
    handleInlineCreationClose();

    // Optional callback
    props.onRecordCreated?.(newRecord);
  };

  return (
    <ReferenceSelectorView
      {...props}
      selectedItems={props?.selectedItems}
      onRemoveItem={onRemoveItem}
      showDownloadButton={props?.showDownloadButton}
      onOptionSelect={onOptionSelect}
      searchKey={searchKey}
      onShowAllResult={onShowAllResult}
      setSearchKey={setSearchKey}
      isShowAllResultsViewOpen={isShowAllResultsViewOpen}
      handleOnAddClick={handleOnAddClick}
      setIsLoading={setIsLoading}
      isLoading={isLoading}
      // Inline creation props
      isInlineCreationDialogOpen={isInlineCreationDialogOpen}
      handleInlineCreationOpen={handleInlineCreationOpen}
      handleInlineCreationClose={handleInlineCreationClose}
      handleRecordCreated={handleRecordCreated}
      hasCreatePermission={hasCreatePermission}
    />
  );
};
